import { ShieldAlert, ExternalLink } from "lucide-react";
import { SectionCard } from "@/components/report/SectionCard";
import { StatusNotice } from "@/components/report/StatusNotice";
import { useLang } from "@/lib/i18n";

export const ViolationsSection = ({ report, lang }) => {
  const { t } = useLang();
  const block = report.violations || { status: "unavailable", items: [], sources: [] };
  const items = block.items || [];
  const src = (block.sources || [])[0];

  let body;
  if (block.status !== "ok") {
    body = <StatusNotice status="unavailable" testid="violations-unavailable" />;
  } else if (items.length === 0) {
    body = <StatusNotice status="none" testid="violations-none" />;
  } else {
    body = (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="py-2 pr-3 font-medium">{t("viol.case")}</th>
              <th className="py-2 pr-3 font-medium">{t("viol.type")}</th>
              <th className="py-2 pr-3 font-medium">{t("viol.opened")}</th>
              <th className="py-2 pr-3 font-medium">{t("viol.status")}</th>
              <th className="py-2 font-medium">{t("viol.lien")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((v, i) => (
              <tr key={`${v.case_number}-${i}`} data-testid="violation-row" className="border-b border-border/60 last:border-0 align-top">
                <td className="py-3 pr-3 font-mono text-xs font-semibold text-foreground">{v.case_number}</td>
                <td className="py-3 pr-3 text-foreground">{v.violation_type}</td>
                <td className="py-3 pr-3 font-mono text-xs text-muted-foreground">{v.date_opened || "—"}</td>
                <td className="py-3 pr-3">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${v.is_open ? "bg-amber-500/10 text-amber-600 dark:text-amber-400" : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    {v.status}
                  </span>
                </td>
                <td className="py-3 font-mono text-[10px] text-muted-foreground">{v.lien ? (v.lien_type || t("viol.lien")) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <SectionCard
      icon={ShieldAlert}
      title={t("sec.violations")}
      count={block.status === "ok" ? items.length : undefined}
      source={src}
      retrievedAt={report.retrieved_at}
      lang={lang}
      testid="section-violations"
    >
      {body}
      {src?.endpoint && (
        <a href={`${src.endpoint}/query?where=1%3D1&f=html`} target="_blank" rel="noopener noreferrer"
           className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline">
          {t("src.view")} <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </SectionCard>
  );
};
